<template>
    <div class="header-search d-flex">
        <div class="me-2">
            <select v-model="searchType" class="form-select">
                <option value="product">{{ $t('product-label') }}</option>
                <option value="property">{{ $t('property-label') }}</option>
            </select>
        </div>

        <div class="me-2 flex-grow-1 position-relative">
            <input
                v-model="searchQuery"
                :placeholder="$t('search')"
                class="form-control"
                @input="onInput"
                @keyup.enter="onHeaderSearch"
            />

            <div v-if="showAutocomplete" class="d-none d-md-flex autocomplete row g-2" @mouseleave="showAutocomplete = false" @mouseenter="showAutocomplete = true">
                <div style="min-height: 20rem" v-if="isLoading">
                    <PreLoader/>
                </div>
                <div class="col-lg-4 m-0 mb-2" v-for="product in products" :key="product.id" v-if="products">
                    <MarketplaceAdMini
                        :id="product.id"
                        :labelnew="true"
                        :labeldiscount="true"
                        :title="product.title"
                        :category="product.categories.length > 0 ? $t(product.categories[0].title) : ''"
                        :regularamount="product.price"
                        :discountedamount="product.promoPrice"
                        :is-vip="product.isVip"
                        :image="product.mainImage !== null ? product.mainImage.src : ''"
                    />
                </div>
                <div class="col-lg-4 m-0 mb-2" v-for="property in properties" :key="property.id" v-if="properties">
                    <PropertyAdMini
                        :id="property.id"
                        :title="property.title"
                        :regularamount="property.price"
                        :image="property.images.length > 0 ? property.images[0].src : ''"
                    />
                </div>
                <div class="mb-3" @click="handleViewAll" v-if="(products && products.length > 0) || (properties && properties.length > 0)">
                    <Button :size="'header'" :theme="'outline-orange'" :text="$t('view-all')" :svg="''" style="width: 200px"/>
                </div>
            </div>
        </div>

        <div>
            <Button
                @click="onHeaderSearch"
                :size="'medium'"
                :theme="'solid-btn-orange'"
                :text="''"
                :svg="'search-white'"
            />
        </div>
    </div>
</template>

<script>
import Button from '@/components/Common/Buttons/Button/Button.vue';
import MarketplaceAdMini from "@/components/Components/MarketplaceAdMini/MarketplaceAdMini.vue";
import PropertyAdMini from "@/components/Components/PropertyAdMini/PropertyAdMini.vue";
import PreLoader from "@/components/Common/PreLoader.vue";

export default {
    name: 'HeaderSearch',
    components: {PreLoader, PropertyAdMini, MarketplaceAdMini, Button },
    data() {
        return {
            searchType: 'product',
            searchQuery: '',
            products: [],
            properties: [],
            debounceTimeout: null,
            showAutocomplete: false,
            isLoading: false,
            // When true, ignore any in-flight responses and prevent state updates until user types again
            suspended: false,
            // Sequence id to invalidate older requests; only the latest id may update state
            requestSeq: 0,
        };
    },
    mounted() {
        this.searchQuery = this.$route.query.s || '';
    },
    watch: {
        '$route'() {
            this.searchQuery = this.$route.query.s || '';
            if (this.$route.name === 'SingleProductPage' || this.$route.name === 'SinglePropertyPage') {
                this.showAutocomplete = false;
            }
        },
        searchType() {
            if (this.suspended) return; // do nothing while suspended
            this.products = [];
            this.properties = [];
            this.showAutocomplete = false;
            if (this.searchQuery && this.searchQuery.length > 0) {
                this.fetchAutocomplete();
            }
        },
        searchQuery(newValue) {
            clearTimeout(this.debounceTimeout);
            if (this.suspended) return; // ignore programmatic changes while suspended

            if (!newValue.trim()) {
                this.products = [];
                this.properties = [];
                this.showAutocomplete = false;
                return;
            }

            this.debounceTimeout = setTimeout(() => {
                this.fetchAutocomplete();
            }, 500);
        },
    },
    methods: {
        onInput() {
            // Resume processing on fresh input after a navigation
            if (this.suspended) {
                this.suspended = false;
            }
        },
        handleViewAll() {
            // Suspend processing of any in-flight autocomplete responses during navigation
            this.suspended = true;
            this.requestSeq++; // invalidate ongoing requests
            this.showAutocomplete = false;
            clearTimeout(this.debounceTimeout);
            this.isLoading = false;
            const s = this.searchQuery;
            if (this.searchType === 'property') {
                this.$router.push({ name: 'PropertySearchPage', query: { s } });
            } else {
                this.$router.push({
                    name: 'MarketplaceCategoryPageKeywordSearch',
                    params: { id: 'new' },
                    query: { s },
                });
            }
        },
        onHeaderSearch() {
            const s = (this.searchQuery || '').trim();
            if (!s) return;
            this.showAutocomplete = false;
            this.handleViewAll();
        },
        async fetchAutocomplete() {
            if (this.suspended) return; // do nothing while suspended
            this.showAutocomplete = true;
            // Invalidate previous requests and capture this request's sequence id
            const seq = ++this.requestSeq;
            this.isLoading = true;
            this.products = [];
            this.properties = [];
            try {
                if (this.searchType === 'property') {
                    await this.fetchPropertyAutocomplete(seq);
                } else {
                    await this.fetchProductAutocomplete(seq);
                }
            } catch (e) {
                console.error(e);
            } finally {
                // Only end loading if this is still the latest request and not suspended
                if (seq === this.requestSeq && !this.suspended) {
                    this.isLoading = false;
                }
            }
        },
        async fetchPropertyAutocomplete(seq) {
            const keyword = this.searchQuery;
            const props = await this.$store.dispatch('property/getProperties', {keyword, offset: 0, limit: 6});
            // If a newer request started or we're suspended, ignore this response
            if (seq !== this.requestSeq || this.suspended) return;
            this.properties = props;
            this.products = null;
        },
        async fetchProductAutocomplete(seq) {
            // if (this.$route.name === 'MarketplaceCategoryPageKeywordSearch' || this.$route.name === 'PropertySearchPage') {
            //   alert(this.$route.name);
            //   return;
            // }
            this.properties = null;

            const result = await this.$store.dispatch('product/searchProduct', {
                offset: 0,
                limit: 6,
                request: { keyword: this.searchQuery },
            });
            // If a newer request started or we're suspended, ignore this response
            if (seq !== this.requestSeq || this.suspended) return;
            this.products = result.data || [];
            this.showAutocomplete = this.products.length > 0;
        },
    },
};
</script>
<style lang="scss" scoped>

.header-search {
    border: 1px solid #ccc;
    border-radius: 50px;
    padding: 5px;
    position: relative;

    select {
        background-color: #f1f1f1;
        color: #333;
        border: none;
        outline: none;
        border-radius: 50px;
        font-size: 16px;
        cursor: pointer;
        width: inherit;

        &:focus {
            outline: none;
        }
    }
    input {
        border: 0;
        outline: 0;
    }
}
.autocomplete {
    position: absolute;
    background-color: #fff;
    bottom: -10px;
    width: calc(50vw);
    min-width: 650px;
    transform: translateY(100%);
    padding: 10px 5px 0 5px;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;

    @media (min-width: 576px) {

    }
}
@media (max-width: 1099px) {
    .autocomplete {
        display: none !important;
    }
}
</style>
